import nacl from 'tweetnacl';
import { v4 as uuidv4 } from 'uuid';

export interface UserIdentity {
  publicKey: string;
  privateKey: string;
  userId: string;
  createdAt: number;
}

export function arrayBufferToBase64(buffer: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < buffer.byteLength; i++) {
    binary += String.fromCharCode(buffer[i]);
  }
  return btoa(binary);
}

export function base64ToArrayBuffer(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export function generateIdentity(): UserIdentity {
  const keyPair = nacl.sign.keyPair();
  
  const publicKey = arrayBufferToBase64(keyPair.publicKey);
  const privateKey = arrayBufferToBase64(keyPair.secretKey);
  
  return {
    publicKey,
    privateKey,
    userId: uuidv4(),
    createdAt: Date.now()
  };
}

export function loadOrCreateIdentity(): UserIdentity {
  const stored = localStorage.getItem('authenticulture_identity');
  
  if (stored) {
    return JSON.parse(stored);
  }
  
  const newIdentity = generateIdentity();
  localStorage.setItem('authenticulture_identity', JSON.stringify(newIdentity));
  
  return newIdentity;
}

export function deriveBoxKeyPair(identity: UserIdentity): { publicKey: Uint8Array; secretKey: Uint8Array } {
  const secretKey = base64ToArrayBuffer(identity.privateKey);
  const seed = secretKey.slice(0, 32);
  // Hash for domain separation: Ed25519 seed → Curve25519 scalar (SHA-512 first 32 bytes)
  const hashed = nacl.hash(seed);
  return nacl.box.keyPair.fromSecretKey(hashed.slice(0, 32));
}

export function signMessage(message: string, privateKey: string): string {
  const secretKey = base64ToArrayBuffer(privateKey);
  const messageBuffer = new TextEncoder().encode(message);
  const signature = nacl.sign.detached(messageBuffer, secretKey);
  return arrayBufferToBase64(signature);
}

export function verifySignature(message: string, signature: string, publicKey: string): boolean {
  try {
    const publicKeyBuffer = base64ToArrayBuffer(publicKey);
    const messageBuffer = new TextEncoder().encode(message);
    const signatureBuffer = base64ToArrayBuffer(signature);
    
    return nacl.sign.detached.verify(messageBuffer, signatureBuffer, publicKeyBuffer);
  } catch (error) {
    console.error('Verify error:', error);
    return false;
  }
}
